import React, {Fragment, useContext, useEffect, useState} from "react";
import DataTable, {TableColumn} from "react-data-table-component";
import axiosConfig from "../../../../Config/axios";
import Loader from "../../../../Layout/Loader";
import IconBtn from "../../../../CommonElements/IconButton";
import AuthorizationContext from "../../../../_helper/Authorization";
import {getSystemCoreRoute} from "../../../../Route/RouteIndex";
import type {AuthorizationContextType} from "../../../../_helper/Authorization/AuthorizationTypes";
import type {Contact, ContactTableProps, ContactTableRef} from "../Types/ContactTypes";

const ContactTable = React.forwardRef<ContactTableRef, ContactTableProps>(({
                                                                               params = {},
                                                                               onEdit,
                                                                               related_key,
                                                                               related_id,
                                                                           }, ref) => {
    const [loaderVisibility, setLoaderVisibility] = useState<boolean>(false);
    const [contacts, setContacts] = useState<Contact[]>([]);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [perPage, setPerPage] = useState<number>(10);
    const [totalRows, setTotalRows] = useState<number>(0);

    const authContext = useContext(AuthorizationContext) as AuthorizationContextType;
    const {errorHandler, hasPermission} = authContext || {};
    const canReadContact = hasPermission ? hasPermission('read-contact') : false;

    React.useImperativeHandle(ref, () => ({
        getContacts,
        addContact,
    }));

    const resolveContactTypeName = (contact: Contact) => {
        return contact?.contact_type?.name || contact?.contactType?.name || '';
    };

    const resolveOwnerName = (contact: Contact) => {
        return contact?.owner?.name || contact?.owner?.user?.name || contact?.owner?.full_name || contact?.owner?.title || '';
    };

    const getContacts = (page: number = currentPage, limit: number = perPage) => {
        if (!canReadContact) return;
        const normalizedParams: Record<string, unknown> = {
            ...params,
            page,
            per_page: limit,
        };

        if (typeof params?.type === 'string' && !normalizedParams.related_key) {
            normalizedParams.related_key = params.type;
        }
        if (typeof params?.id !== 'undefined' && !normalizedParams.related_id) {
            normalizedParams.related_id = params.id;
        }
        if (related_key) {
            normalizedParams.related_key = related_key;
        }
        if (typeof related_id !== 'undefined') {
            normalizedParams.related_id = related_id;
        }

        setLoaderVisibility(true);
        axiosConfig
            .get(getSystemCoreRoute('contact'), {params: normalizedParams})
            .then((response: any) => {
                const responseData = response?.data?.data;
                const list = responseData?.data || responseData || [];

                if (Array.isArray(list)) {
                    setContacts(list);
                }

                if (responseData?.meta) {
                    setCurrentPage(responseData.meta.current_page || 1);
                    setPerPage(responseData.meta.per_page || limit);
                    setTotalRows(responseData.meta.total || list.length);
                } else {
                    setCurrentPage(page);
                    setPerPage(limit);
                    setTotalRows(Array.isArray(list) ? list.length : 0);
                }
                setLoaderVisibility(false);
            })
            .catch((exception: any) => {
                errorHandler(exception);
                setLoaderVisibility(false);
            });
    };

    const deleteContact = (id: number) => {
        setLoaderVisibility(true);
        axiosConfig
            .delete(`${getSystemCoreRoute('contact')}/${id}`)
            .then((response: any) => {
                const deletedId = response?.data?.data?.id;
                if (deletedId) {
                    setContacts((current) => current.filter((item) => item.id !== deletedId));
                }
                setLoaderVisibility(false);
            })
            .catch((exception: any) => {
                errorHandler(exception);
                setLoaderVisibility(false);
            });
    };

    const addContact = (contact: Contact) => {
        const contactList = [...contacts];
        const existingContactIndex = contactList.findIndex((existingContact) => existingContact.id === contact.id);
        if (existingContactIndex !== -1) {
            contactList[existingContactIndex] = contact;
        } else {
            contactList.unshift(contact);
        }
        setContacts(contactList);
    };

    useEffect(() => {
        if (!canReadContact) {
            setContacts([]);
            return;
        }
        getContacts(1, perPage);
    }, [params, related_key, related_id, canReadContact]);

    if (!canReadContact) {
        return <Fragment/>;
    }

    const columns: TableColumn<Contact>[] = [
        {
            name: 'ID',
            selector: (row: Contact) => row.id,
            sortable: true,
            width: '90px',
        },
        {
            name: 'Owner',
            selector: (row: Contact) => resolveOwnerName(row),
            sortable: false,
        },
        {
            name: 'Type',
            selector: (row: Contact) => resolveContactTypeName(row),
            sortable: false,
        },
        {
            name: 'Contact',
            selector: (row: Contact) => row.contact,
            sortable: false,
        },
        {
            name: 'Option',
            cell: (row: Contact) => (
                <div className='d-flex align-items-right gap-2'>
                    <IconBtn type={'edit'} attrBtn={{onClick: () => onEdit?.(row)}} permission={'update-contact'}/>
                    <IconBtn type={'delete'} confirm={true} onclick={() => deleteContact(row.id)}
                             permission={'delete-contact'}/>
                </div>
            ),
            sortable: false,
            right: true,
        },
    ];

    return <Fragment>
        <Loader visibility={loaderVisibility}/>
        <DataTable pagination paginationServer columns={columns}
                   data={contacts}
                   paginationTotalRows={totalRows}
                   paginationPerPage={perPage}
                   paginationDefaultPage={currentPage}
                   onChangePage={(page) => getContacts(page, perPage)}
                   onChangeRowsPerPage={(newPerPage, page) => {
                       setPerPage(newPerPage);
                       getContacts(page, newPerPage);
                   }}
                   highlightOnHover={true}
                   striped={true} responsive={true}/>
    </Fragment>;
});

export default ContactTable;

